/**
 * PrintableSpecModal — wizard V4 générique (bascule Gelato 2026-05-17).
 *
 * V4 : 1 formulaire unique au lieu de cascade typée par `kind` :
 *   - `sku` (productUid Gelato sémantique — ex
 *     `posters_pf_a3_pt_mat_cl_4-4_ver`)
 *   - `attributes` (objet JSON libre — paper/color/orientation/…)
 *   - `label_override` optionnel (sinon dérivé du provider_display_name
 *     + premières attributes côté BE)
 *
 * Le BE :
 *   1. ensure le SKU productUid en cache (`ensure_sku_in_cache` → hit
 *      Gelato `GET /v3/products/{productUid}` si absent)
 *   2. calcule `attributes_signature` SHA256 (canonical JSON)
 *   3. INSERT spec parent + 1 variant enfant initial (taille de ce SKU)
 *
 * Plus de cascade typée paper → frame → mount → glaze : Gelato encode
 * tous ces attributs dans le productUid lui-même. L'utilisatrice copie
 * le productUid depuis le catalogue Gelato (dashboard ou doc) — pas de
 * recherche fuzzy V1 (à enrichir si le besoin se confirme).
 */

import { useMemo, useState } from 'react'
import { Controller, useForm, type SubmitHandler } from 'react-hook-form'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import Modal from '@/components/ui/Modal'
import Textarea from '@/components/ui/Textarea'
import { useActivePodCatalogSkus } from '@/hooks/usePodCatalog'
import { useCreatePrintableSpec } from '@/hooks/usePrintableSpecs'
import {
  printableSpecCreateSchema,
  type PrintableSpecCreateValues,
} from '@/schemas/printable-specs'
import type { PodSkuCatalogResponse } from '@/utils/api/pod-catalog'

import './PrintableSpecModal.css'

const MAX_SUGGESTIONS = 8

interface ApiError {
  status?: number
  detail?: unknown
}

interface Props {
  onClose: () => void
  onSaved: (specId: number) => void
}

type FormValues = {
  provider_slug: 'gelato'
  sku: string
  attributesJson: string
  label_override: string
}

export default function PrintableSpecModal({ onClose, onSaved }: Props) {
  const createMutation = useCreatePrintableSpec()
  const catalogQuery = useActivePodCatalogSkus()
  const [parseError, setParseError] = useState<string | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)

  const form = useForm<FormValues>({
    defaultValues: {
      provider_slug: 'gelato',
      sku: '',
      attributesJson: '{}',
      label_override: '',
    },
    mode: 'onSubmit',
  })

  const skuValue = form.watch('sku')
  const allSkus = useMemo(() => catalogQuery.data?.items ?? [], [catalogQuery.data])

  const skuPreview: PodSkuCatalogResponse | null = useMemo(() => {
    const trimmed = skuValue.trim()
    if (!trimmed) return null
    return allSkus.find((row) => row.sku === trimmed) ?? null
  }, [skuValue, allSkus])

  const suggestions = useMemo(() => {
    const needle = skuValue.trim().toLowerCase()
    if (!needle) return []
    return allSkus
      .filter((row) => {
        if (row.sku === skuValue.trim()) return false
        return (
          row.sku.toLowerCase().includes(needle) ||
          row.provider_display_name.toLowerCase().includes(needle) ||
          row.category.toLowerCase().includes(needle)
        )
      })
      .slice(0, MAX_SUGGESTIONS)
  }, [skuValue, allSkus])

  const submit: SubmitHandler<FormValues> = (values) => {
    setParseError(null)
    form.clearErrors(['sku', 'label_override'])

    let parsedAttributes: Record<string, string | number | boolean | null>
    try {
      const raw = JSON.parse(values.attributesJson || '{}') as unknown
      if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
        throw new Error('Attributes doit être un objet JSON (clé/valeur)')
      }
      parsedAttributes = raw as Record<string, string | number | boolean | null>
    } catch (err) {
      const message = err instanceof Error ? err.message : 'JSON invalide'
      setParseError(message)
      return
    }

    const validated = printableSpecCreateSchema.safeParse({
      provider_slug: 'gelato',
      sku: values.sku,
      attributes: parsedAttributes,
      label_override: values.label_override || undefined,
    } satisfies PrintableSpecCreateValues)

    if (!validated.success) {
      // Route chaque issue Zod vers le champ concerné — sinon une erreur
      // sku (ex: cap longueur) s'affichait sous le champ JSON.
      const attributesIssues: string[] = []
      for (const issue of validated.error.issues) {
        const field = issue.path[0]
        if (field === 'sku') {
          form.setError('sku', { type: 'manual', message: issue.message })
        } else if (field === 'label_override') {
          form.setError('label_override', { type: 'manual', message: issue.message })
        } else {
          attributesIssues.push(issue.message)
        }
      }
      if (attributesIssues.length > 0) {
        setParseError(attributesIssues.join(' · '))
      }
      return
    }

    createMutation.mutate(validated.data, {
      onSuccess: (row) => {
        const count = row.variants.length
        toast.success(
          `Spec « ${row.label_cache} » créée — ${count} variante${count > 1 ? 's' : ''}`,
        )
        onSaved(row.id)
      },
      onError: (err) => {
        toast.error(extractDetailMessage(err) ?? 'Création impossible')
      },
    })
  }

  return (
    <Modal isOpen onClose={onClose} title="Nouvelle config production" kicker="Gelato" size="form">
      <form onSubmit={form.handleSubmit(submit)} noValidate>
        <Modal.Body>
          <div className="ui-stack">
            <p className="ui-body ui-text-muted">
              Saisis le <code>productUid</code> Gelato (visible dans le dashboard ou la doc) et les
              attributes opaques associés. La variante initiale (taille de ce productUid) est créée
              automatiquement ; tu peux ajouter d&apos;autres tailles après création.
            </p>

            <Field
              label="productUid Gelato"
              required
              helper={
                catalogQuery.isLoading
                  ? 'Chargement du cache catalogue…'
                  : 'Format Gelato sémantique, ex: posters_pf_a3_pt_mat_cl_4-4_ver'
              }
              error={form.formState.errors.sku?.message}
            >
              {(props) => (
                <div className="printable-spec-modal-combobox">
                  <Controller
                    name="sku"
                    control={form.control}
                    rules={{ required: 'productUid requis' }}
                    render={({ field }) => (
                      <Input
                        type="text"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder="posters_pf_a3_pt_mat_cl_4-4_ver"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e)
                          setShowSuggestions(true)
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => {
                          // Délai pour permettre le clic sur une suggestion.
                          window.setTimeout(() => setShowSuggestions(false), 120)
                        }}
                        {...props}
                      />
                    )}
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <ul
                      className="printable-spec-modal-suggestions"
                      role="listbox"
                      aria-label="Suggestions productUid"
                    >
                      {suggestions.map((row) => (
                        <li key={row.sku}>
                          <Button
                            type="button"
                            variant="quiet"
                            className="printable-spec-modal-suggestion"
                            onClick={() => {
                              form.setValue('sku', row.sku, { shouldValidate: true })
                              setShowSuggestions(false)
                            }}
                          >
                            <code className="printable-spec-modal-suggestion-sku">{row.sku}</code>
                            <span className="ui-text-muted printable-spec-modal-suggestion-meta">
                              {row.provider_display_name} · {row.width_cm}×{row.height_cm} cm
                            </span>
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </Field>

            {skuPreview && (
              <aside className="printable-spec-modal-preview" aria-live="polite">
                <strong>{skuPreview.provider_display_name}</strong>
                <span className="ui-text-muted">
                  {skuPreview.category} · {skuPreview.width_cm}×{skuPreview.height_cm} cm
                  {skuPreview.base_cost_cents_eur != null && (
                    <> · base {(skuPreview.base_cost_cents_eur / 100).toFixed(2)} €</>
                  )}
                </span>
              </aside>
            )}

            <Field
              label="Attributes (JSON)"
              required
              helper="Objet JSON libre — clés/valeurs qui différencient cette spec d'une autre sur le même productUid (ex: paper, color, orientation)."
              error={parseError ?? undefined}
            >
              {(props) => (
                <Controller
                  name="attributesJson"
                  control={form.control}
                  render={({ field }) => (
                    <Textarea
                      rows={6}
                      placeholder='{"paper": "mat", "orientation": "vertical"}'
                      {...field}
                      {...props}
                    />
                  )}
                />
              )}
            </Field>

            <Field
              label="Label affiché (optionnel)"
              helper="Sinon dérivé du nom Gelato + premières attributes."
              error={form.formState.errors.label_override?.message}
            >
              {(props) => (
                <Controller
                  name="label_override"
                  control={form.control}
                  render={({ field }) => (
                    <Input
                      type="text"
                      placeholder="A3 Poster mat, vertical"
                      {...field}
                      {...props}
                    />
                  )}
                />
              )}
            </Field>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button type="button" variant="quiet" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Création…' : 'Créer la config'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

function extractDetailMessage(err: ApiError): string | null {
  if (typeof err.detail === 'string') return err.detail
  if (typeof err.detail === 'object' && err.detail !== null && 'message' in err.detail) {
    const m = (err.detail as { message?: unknown }).message
    if (typeof m === 'string') return m
  }
  return null
}
